import React, { useMemo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { LinearProgress } from '@material-ui/core';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';
import * as Yup from 'yup';

import { t } from '~/helpers';
import api from '~/services/api';

import { Header, Table } from './components';
import * as S from './styles';

function RelPitStopFatorConsumo() {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [textoPesquisa, setTextoPesquisa] = useState('');
  const [tableOptions, setTableOptions] = useState({
    agrupamento: 'L',
    agrupaPorDia: 0,
  });
  const [lastReportParams, setLastReportParams] = useState(null);

  const getTipoCargaLabel = useCallback((tipoCarga) => {
    const labels = {
      A: t('s.relatorio.geral.todos'),
      T: t('s.relatorio.lubrificacaolistagem.troca'),
      C: t('s.relatorio.pitstopfatorconsumo.complemento'),
    };

    return labels[tipoCarga] || tipoCarga;
  }, []);

  const header = useMemo(() => {
    const agora = moment();

    return {
      datainicio: agora
        .clone()
        .startOf('month')
        .format('DD/MM/YYYY 00:00:00'),
      datafim: agora.clone().format('DD/MM/YYYY 23:59:59'),
      agrupamento: 'L', // 'L' - Locomativa, 'M' - Modelo
      idveiculo: '',
      idmodeloveiculo: 0,
      tipoCarga: 'A', // 'A' - Todos, 'T' - Troca, 'C' - Complemento
      agrupaPorDia: '0',
    };
  }, []);

  const schema = useMemo(() =>
    Yup.object().shape({
      datainicio: Yup.mixed()
        .transform((value) =>
          value ? moment(value, 'DD/MM/YYYY HH:mm:ss') : null
        )
        .required(t('s.geral.validacao.campoobrigatorio')),
      datafim: Yup.mixed()
        .transform((value) =>
          value ? moment(value, 'DD/MM/YYYY HH:mm:ss') : null
        )
        .required(t('s.geral.validacao.campoobrigatorio'))
        .test(
          'datafimmaior',
          t('s.relatorio.pitstopfatorconsumo.datainicialmaiorfinal'),
          function dataFimMaior(value) {
            const { datainicio } = this.parent;
            const dataInicio = moment(datainicio, 'DD/MM/YYYY 00:00:00');
            const dataFim = moment(value, 'DD/MM/YYYY 23:59:59');

            return dataFim.isValid() && dataInicio.isValid()
              ? dataFim.isAfter(dataInicio)
              : true;
          }
        ),
      agrupamento: Yup.string().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
      idveiculo: Yup.string().nullable(),
      idmodeloveiculo: Yup.string().nullable(),
      tipoCarga: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
      agrupaPorDia: Yup.number().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
    })
  );

  const handleSubmitHeader = useCallback(async (formData) => {
    try {
      setLoading(true);

      await schema.validate(formData, {
        abortEarly: false,
      });

      const {
        datainicio,
        datafim,
        agrupamento,
        idveiculo,
        idmodeloveiculo,
        tipoCarga,
        agrupaPorDia,
      } = formData;

      const dataInicioMoment = moment(datainicio, 'DD/MM/YYYY HH:mm:ss');
      const dataFimMoment = moment(datafim, 'DD/MM/YYYY HH:mm:ss');

      if (!dataFimMoment.isAfter(dataInicioMoment)) {
        toast.error(
          t('s.operacao.descarganf.datahorainicialmaiordatahorafinal')
        );
        return;
      }

      const dataInicio = dataInicioMoment.format('YYYY-MM-DD HH:mm:ss');
      const dataFim = dataFimMoment.format('YYYY-MM-DD HH:mm:ss');

      const agrupaPorDiaValue = Number(agrupaPorDia);

      const params = {
        dataInicio,
        dataFim,
        agrupamento,
        idveiculo: agrupamento === 'L' ? idveiculo || '' : '',
        idmodeloveiculo:
          agrupamento === 'M' && Number(idmodeloveiculo) > 0
            ? Number(idmodeloveiculo)
            : 0,
        tipoCarga,
        agrupaPorDia: agrupaPorDiaValue,
      };

      setLastReportParams(params);
      setTableOptions({
        agrupamento,
        agrupaPorDia: agrupaPorDiaValue,
      });

      let filtro =
        `FILTRO - ${t('s.geral.opcoes.datahorainicial')}: ${datainicio} | ` +
        `${t('s.geral.opcoes.datahorafinal')}: ${datafim}`;

      filtro += ` | ${t('Agrupamento')}: ${agrupamento === 'L' ? t('Locomotiva') : t('Modelo')
        }`;

      if (agrupamento === 'L' && idveiculo) {
        filtro += ` | ${t('s.menu.cadastro.veiculo.locomotiva')}: ${idveiculo}`;
      }

      if (agrupamento === 'M' && Number(idmodeloveiculo) > 0) {
        filtro += ` | ${t('Modelo')}: ${idmodeloveiculo}`;
      }

      filtro += ` | ${t('Tipo de Carga')}: ${getTipoCargaLabel(tipoCarga)}`;
      filtro += ` | ${t('Agrupar por dia')}: ${agrupaPorDiaValue === 1
        ? t('s.geral.opcoes.sim')
        : t('s.geral.opcoes.nao')
        }`;

      setTextoPesquisa(filtro);

      const { data } = await api.get('pitstop/relatorio/fatorconsumo', {
        params,
        paramsSerializer: (param) => qs.stringify(param),
      });

      setDataTable(data);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          if (error?.message) toast.error(error.message);
        });
      } else if (isArray(err)) {
        err.map((e) => toast.error(t(e)));
      } else if (typeof err === 'string') {
        toast.error(t(err));
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [getTipoCargaLabel, schema]);

  const handleExportData = useCallback(
    async (formato) => {
      try {
        setLoading(true);

        if (!lastReportParams) {
          return;
        }

        const fieldList = [];
        if (lastReportParams.agrupaPorDia === 1) {
          fieldList.push('dataencosteatendimento');
        }
        if (lastReportParams.agrupamento === 'L') {
          fieldList.push('idveiculo');
        }
        fieldList.push(
          'nomemodeloveiculoabastecimento',
          'volumemovimentacaoabastecimento',
          'volumemovimentacaolubrificacao',
          'fatorconsumo'
        );

        const tituloRelatorio = t('s.menu.relatorio.relpitstopfatorconsumo');
        const reportRequestParams = {
          fieldlist: fieldList.join(','),
          relatorio: {
            custom: 'true',
            formato,
            tituloRelatorio,
            textoPesquisa,
          },
        };

        const response = await api.post(
          'pitstop/relatorio/fatorconsumo/export',
          reportRequestParams,
          {
            params: lastReportParams,
            paramsSerializer: (param) => qs.stringify(param),
            responseType: 'blob',
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;

        if (formato.toLowerCase() === 'excel') {
          link.download = `${tituloRelatorio}.xls`;
        } else {
          link.target = '_blank';
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        if (Array.isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [lastReportParams, textoPesquisa]
  );

  return (
    <S.Wrapper>
      {loading && <LinearProgress />}
      <Header
        data={header}
        loading={loading}
        handleSubmit={handleSubmitHeader}
        canExport={dataTable.length > 0}
        onExportClick={handleExportData}
      />

      <Table
        rows={dataTable}
        agrupamento={tableOptions.agrupamento}
        agrupaPorDia={tableOptions.agrupaPorDia}
      />
    </S.Wrapper>
  );
}

export default RelPitStopFatorConsumo;
